import React, { useState, useEffect, useRef } from 'react';
import { Columns3, Eye, EyeOff, Check } from 'lucide-react';

export interface ColumnOption<T extends string = string> {
    key: T;
    label: string;
    icon?: React.ComponentType<any>;
    color?: string;
}

export interface ColumnVisibilityDropdownProps<T extends string = string> {
    columns: ColumnOption<T>[];
    visibleColumns: Record<T, boolean>;
    onChange: (visibleColumns: Record<T, boolean>) => void;
    onToggle?: (key: T) => void;
    buttonClassName?: string;
    dropdownClassName?: string;
    align?: 'left' | 'right';
    buttonText?: string;
}

export function ColumnVisibilityDropdown<T extends string = string>({
    columns,
    visibleColumns,
    onChange,
    onToggle,
    buttonClassName = "p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 text-gray-200 transition-all flex items-center gap-2",
    dropdownClassName = "w-52",
    align = 'right',
    buttonText = "Columns",
}: ColumnVisibilityDropdownProps<T>) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleToggle = (key: T) => {
        if (onToggle) {
            onToggle(key);
        }
        onChange({
            ...visibleColumns,
            [key]: !visibleColumns[key],
        });
    };

    const allVisible = columns.every((col) => visibleColumns[col.key]);

    const handleToggleAll = () => {
        const nextState = { ...visibleColumns };
        columns.forEach((col) => {
            nextState[col.key] = !allVisible;
        });
        onChange(nextState);
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={buttonClassName}
            >
                <Columns3 className="w-3.5 h-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">{buttonText}</span>
            </button>
            {isOpen && (
                <div
                    className={`absolute top-full mt-2 ${align === 'right' ? 'right-0' : 'left-0'} ${dropdownClassName} bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto`}
                >
                    <div className="px-3 py-1.5 text-[9px] font-bold text-gray-100 uppercase border-b border-gray-800 mb-1 flex items-center justify-between">
                        <span>Toggle Columns</span>
                        <button
                            type="button"
                            onClick={handleToggleAll}
                            className="text-[9px] text-orange-400 hover:text-orange-300 transition-colors"
                        >
                            {allVisible ? 'Hide All' : 'Show All'}
                        </button>
                    </div>
                    {columns.map((option) => {
                        const isVisible = !!visibleColumns[option.key];
                        return (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() => handleToggle(option.key)}
                                className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-400 hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                <div className="flex items-center gap-2.5">
                                    {isVisible ? (
                                        <Eye className="w-3 h-3 text-orange-500" />
                                    ) : (
                                        <EyeOff className="w-3 h-3 text-gray-600" />
                                    )}
                                    {option.label}
                                </div>
                                <div
                                    className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${
                                        isVisible
                                            ? 'bg-orange-600 border-orange-600'
                                            : 'border-gray-700'
                                    }`}
                                >
                                    {isVisible && <Check className="w-2.5 h-2.5 text-white" />}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default ColumnVisibilityDropdown;
